import { containsFrameStyle, documentOutputRatio, nativeSize, outputContainLayout, type Document, type SourceId } from '@/core/document'
import { planExport } from '@/core/export/planExport'
import type { ExportOptions } from '@/core/export/types'
import { ExportUnsupportedError } from '@/core/export/errors'
import { assertCanvasSizeWithinLimit } from '@/core/render/canvasLimits'
import { renderNode, type NodeRenderSource } from '@/core/render/renderNode'
import { ensureFrameFontsLoaded } from '@/tools/frame/fonts'
import { FrameStyleId } from '@/tools/frame/params'

export type RenderCanvas = OffscreenCanvas | HTMLCanvasElement

export interface RenderSurface<TCanvas extends RenderCanvas = RenderCanvas> {
  canvas: TCanvas
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D
}

export type RenderSurfaceFactory<TCanvas extends RenderCanvas> = (size: { width: number; height: number }) => RenderSurface<TCanvas>

export function createOffscreenRenderSurface(
  size: { width: number; height: number },
  maxCanvasArea?: number,
): RenderSurface<OffscreenCanvas> {
  if (typeof OffscreenCanvas === 'undefined') throw new ExportUnsupportedError()
  if (maxCanvasArea !== undefined) assertCanvasSizeWithinLimit(size, maxCanvasArea)
  const canvas = new OffscreenCanvas(size.width, size.height)
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Document render requires an OffscreenCanvas 2D context')
  return { canvas, context }
}

export function renderDocument(
  document: Document,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  options: ExportOptions,
): Promise<OffscreenCanvas>
export function renderDocument<TCanvas extends RenderCanvas>(
  document: Document,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  options: ExportOptions,
  createSurface: RenderSurfaceFactory<TCanvas>,
): Promise<TCanvas>

export async function renderDocument(
  document: Document,
  sources: ReadonlyMap<SourceId, NodeRenderSource>,
  options: ExportOptions,
  createSurface: RenderSurfaceFactory<RenderCanvas> = createOffscreenRenderSurface,
): Promise<RenderCanvas> {
  const plan = planExport(document, sources, options)
  if (plan.outputs.length === 0) throw new Error('Document render plan did not produce an output')
  if (containsFrameStyle(document.root, FrameStyleId.Polaroid)) await ensureFrameFontsLoaded()

  const rootSize = nativeSize(document, sources, document.root.id)
  const layout = outputContainLayout(rootSize, documentOutputRatio(document, sources))
  const target = createSurface(plan.renderSize)
  const context = target.context
  const scaleX = plan.renderSize.width / layout.size.width
  const scaleY = plan.renderSize.height / layout.size.height
  context.imageSmoothingEnabled = true
  context.imageSmoothingQuality = 'high'
  context.fillStyle = document.output.background
  context.fillRect(0, 0, plan.renderSize.width, plan.renderSize.height)
  context.save()
  context.translate(layout.rootRect.x * scaleX, layout.rootRect.y * scaleY)
  context.scale(scaleX, scaleY)
  renderNode(document.root, document, sources, rootSize, context)
  context.restore()
  return target.canvas
}
